import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { AssignPractitionerServicesIntent } from './assign-practitioner-services.payload';
import { buildAssignPractitionerServicesPayload } from './assign-practitioner-services.payload';

interface UseAssignPractitionerServicesOptions {
  onSuccess?: () => void;
  /** Off for bulk assignment, which raises one summary toast instead. */
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export const useAssignPractitionerServices = (
  options?: UseAssignPractitionerServicesOptions
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      practitionerId,
      ...intent
    }: AssignPractitionerServicesIntent & { practitionerId: string }) =>
      apiClient.put(
        `practitioners/${practitionerId}/services`,
        buildAssignPractitionerServicesPayload(intent)
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['practitioners'] });
      void queryClient.invalidateQueries({
        queryKey: ['practitioners', variables.practitionerId],
      });
      if (options?.showSuccessToast !== false) {
        toast.success('Services updated');
      }
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      if (options?.showErrorToast !== false) {
        toast.error(error.message || 'Failed to assign services');
      }
    },
  });

  return {
    assignServices: mutation.mutate,
    assignServicesAsync: mutation.mutateAsync,
    isAssigning: mutation.isPending,
  };
};
